import { Button, Checkbox, flash, FormGroup, Label, Modal, SubmitButton, Textarea } from "@xola/ui-kit";
import { FormField, InputField } from "common";
import { FormikValues } from "formik";
import { produce } from "immer";
import React, { useState } from "react";
import { useMutation } from "react-query";
import { Experience, Media, MerchandiseCatalogItem, Product, productMap, ProductObject } from "types";
import { boolean, object, string } from "yup";
import { api } from "../../api/api";
import { getFormMediaUrl } from "../../hooks/formMediaUrl";
import { useInvalidate } from "../../hooks/useInvalidation";
import { ImageUploadInput } from "../Common/ImageUploadInput";
import { getXolaImageSize } from "../Common/ProductImage";
import { SecondaryButton } from "../Common/SecondaryButton";
import { XolaFormik } from "../Common/XolaFormik";
import { sanitizeExperiencePayload } from "@/models/Experience";
import { getSeller } from "@/utils/getSeller";

interface UploadProductImageModalProps {
    onClose: (e: any) => void;
    isOpen: boolean;
    product?: Product;
    catalogItem?: MerchandiseCatalogItem;
    media?: Media;
}

export const UploadProductImageModal = ({
    onClose,
    isOpen,
    product,
    catalogItem,
    media,
}: UploadProductImageModalProps) => {
    return (
        <Modal isOpen={isOpen} size="medium" className="!max-w-[510px]" onClose={onClose}>
            <ModalContent product={product} catalogItem={catalogItem} media={media} onClose={onClose} />
        </Modal>
    );
};

interface ModalContentProps {
    onClose: (e?: any) => void;
    product?: Product;
    catalogItem?: MerchandiseCatalogItem;
    media?: Media;
}

const getValidationSchema = () => {
    return object({
        caption: string(),
        isPrimaryPhoto: boolean(),
    });
};

const ModalContent = ({ onClose, product, catalogItem, media }: ModalContentProps) => {
    const seller = getSeller();
    const invalidate = useInvalidate();
    const productOrCatalogItem = product ?? catalogItem;

    const uploadURLPath = product ? `/${productMap[product.object]}/${product.id}` : `/catalogItems/${catalogItem?.id}`;

    const initialValues = {
        caption: media ? media.caption ?? "" : "",
        isPrimaryPhoto: media?.id === productOrCatalogItem?.photo?.id,
    };

    const [activeMedia, setActiveMedia] = useState(media);
    const [previousMedias, setPreviousMedias] = useState<Record<Media["id"], Media>>({});
    const [isUploading, setIsUploading] = useState(false);

    const updatePictureUrl = getFormMediaUrl(`${uploadURLPath}/medias`, catalogItem ? seller.id : undefined);
    const pictureSource = getFormMediaUrl(
        `${uploadURLPath}/medias/${activeMedia?.id}?size=${getXolaImageSize("medium")}`,
    );

    const { mutateAsync: deletePhoto, isLoading: isDeleting } = useMutation(async (mediaId?: ID) => {
        return api.delete<Media>(`${uploadURLPath}/medias/${mediaId}`);
    });

    const { mutateAsync: updateCaption, isLoading: isUpdatingCaption } = useMutation(async (payload: Media) => {
        return api.put<Media>(`${uploadURLPath}/medias/${activeMedia?.id}`, payload);
    });

    const { mutateAsync: setPrimaryPhoto, isLoading: isSettingPrimary } = useMutation(
        async (payload: Product | { photo: Media }) => {
            return api.put<Product | MerchandiseCatalogItem>(
                uploadURLPath,
                product && product.object === ProductObject.EXPERIENCE
                    ? sanitizeExperiencePayload(payload as Experience)
                    : payload,
            );
        },
    );

    if (!productOrCatalogItem) return null;

    const deletePreviousMedias = async ({ all = false, initial = false } = {}) => {
        const mediasToBeDeleted = Object.values(previousMedias).filter((media) => all || media.id !== activeMedia?.id);

        if (initial && media) {
            mediasToBeDeleted.push(media);
        }

        await Promise.all(
            mediasToBeDeleted.map(async (media) => {
                await deletePhoto(media.id);
            }),
        );
    };

    const handleInvalidation = () => {
        if (product) {
            invalidate(productMap[product.object], product.id);
            invalidate("products", product.id);
        } else if (catalogItem) {
            invalidate("catalogItems");
        }
    };

    const handleDelete = async () => {
        await deletePhoto(activeMedia?.id);
        handleInvalidation();
        flash.success("Photo deleted");
        onClose();
    };

    const handleSubmit = async (values: FormikValues) => {
        if (values.caption !== null && activeMedia) {
            const payload = produce(activeMedia, (draft) => {
                draft.caption = values.caption;
            });
            await updateCaption(payload);
        }

        if (values.isPrimaryPhoto && activeMedia && activeMedia.id !== productOrCatalogItem.photo?.id) {
            if (product) {
                const payload =
                    product.object === ProductObject.WARE
                        ? { photo: activeMedia }
                        : produce(product, (draft) => {
                              if (draft.photo) {
                                  draft.photo.id = activeMedia.id; // Gift Offers
                              } else {
                                  draft.photo = activeMedia; // Experiences
                              }
                          });
                await setPrimaryPhoto(payload);
            } else if (catalogItem) {
                await setPrimaryPhoto({ photo: activeMedia });
            }
        }

        await deletePreviousMedias({ initial: activeMedia?.id !== media?.id });

        handleInvalidation();

        if (media) {
            flash.success("Photo updated");
        }

        if (!media && activeMedia) {
            flash.success("Photo added");
        }

        onClose();
    };

    const handleCancel = async () => {
        await deletePreviousMedias({ all: true, initial: false });
        onClose();
    };

    const handleUpload = async (media?: Media) => {
        if (!media) {
            return;
        }

        setPreviousMedias({
            ...previousMedias,
            [media.id]: media,
        });
        setActiveMedia(media);
    };

    const Requirements = ({ maxSize = 5 }: { maxSize?: number }) => {
        return (
            <div>
                <p>
                    Please ensure that the image is in PNG, JPG or WebP format and does not exceed{" "}
                    <var data-var="maxSize">{maxSize}</var>MB
                </p>
                <p>Recommended size : 1024x768</p>
            </div>
        );
    };

    const isLoading = isDeleting || isUpdatingCaption || isSettingPrimary;

    return (
        <XolaFormik
            validateOnChange={false}
            validateOnBlur={false}
            validationSchema={getValidationSchema}
            initialValues={initialValues}
            onSubmit={handleSubmit}
        >
            {({ handleSubmit, setFieldValue, values }) => (
                <>
                    <Modal.Header>{media ? "Edit Photo" : "Upload Photo"}</Modal.Header>

                    <Modal.Body>
                        <ImageUploadInput
                            src={activeMedia ? pictureSource : undefined}
                            uploadUrl={updatePictureUrl}
                            hasDelete={false}
                            requirements={<Requirements />}
                            setIsUploading={setIsUploading}
                            onUpdated={handleUpload}
                            onError={(error: string) => flash.danger(error)}
                        />

                        <FormField name="caption" label="Caption">
                            <InputField as={Textarea} />
                        </FormField>

                        <FormGroup>
                            <Label>Primary Photo</Label>
                            <Checkbox
                                label="Make this the primary photo"
                                checked={values.isPrimaryPhoto}
                                onChange={(e: any) => {
                                    setFieldValue("isPrimaryPhoto", e.target.checked);
                                }}
                            />
                        </FormGroup>
                    </Modal.Body>

                    <Modal.Footer>
                        <fieldset className="flex justify-between space-x-4">
                            <Button disabled={!media} color="danger" className="self-start" onClick={handleDelete}>
                                Delete
                            </Button>

                            <div className="flex justify-end space-x-4">
                                <SecondaryButton onClick={handleCancel}>Cancel</SecondaryButton>
                                <SubmitButton disabled={isUploading} isLoading={isLoading} onClick={handleSubmit}>
                                    Save
                                </SubmitButton>
                            </div>
                        </fieldset>
                    </Modal.Footer>
                </>
            )}
        </XolaFormik>
    );
};
